import { Briefcase, Check, Info } from "lucide-react";
import type { FC } from "react";

import type { ForeignNationalNoteProposal } from "@/api/foreignNationals";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/ui/tag";

import { NoteAvatar, NoteBody } from "./NoteEntry";
import { noteDateLabel } from "./notesModel";

type Props = {
  proposal: ForeignNationalNoteProposal;
  caseLabel?: string | null;
  busy?: boolean;
  onAdd: () => void;
  onDismiss: () => void;
};

/** An assistant suggestion waiting on a person; answering here answers its chat card too. */
export const NoteProposalCard: FC<Props> = ({
  proposal,
  caseLabel,
  busy,
  onAdd,
  onDismiss
}) => (
  <div
    data-testid="note-proposal"
    className="flex gap-3 rounded-xl bg-elevated px-3 pt-3 pb-2.5 ring-1 ring-line-2"
  >
    <NoteAvatar name="Casey" assistant />
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-base font-medium text-fg">Casey</span>
        <span className="text-base text-fg-3">suggests a note</span>
        {proposal.proposed_at ? (
          <span className="text-sm text-fg-4">
            · {noteDateLabel(proposal.proposed_at)}
          </span>
        ) : null}
      </div>
      <NoteBody body={proposal.note} />
      <span className="flex items-center gap-1.5 text-sm text-fg-3">
        <Info className="size-3 shrink-0 text-warn" />
        {proposal.thread_title
          ? `From the chat "${proposal.thread_title}". Read it before adding.`
          : "From a chat with the assistant. Read it before adding."}
      </span>
      <div className="mt-0.5 flex items-center gap-1.5">
        {caseLabel ? (
          <Tag>
            <Briefcase />
            {caseLabel}
          </Tag>
        ) : null}
        {proposal.standalone ? (
          <>
            <Button
              variant="ghost"
              size="xs"
              className="ml-auto"
              disabled={busy}
              onClick={onDismiss}
            >
              Dismiss
            </Button>
            <Button
              variant="secondary"
              size="xs"
              disabled={busy}
              data-testid="note-proposal-add"
              onClick={onAdd}
            >
              <Check />
              Add Note
            </Button>
          </>
        ) : (
          <span className="ml-auto text-sm text-fg-3">
            It came with other actions; answer it in the chat.
          </span>
        )}
      </div>
    </div>
  </div>
);
